import type { XYPosition } from 'react-flow-renderer';
import type { SnippetNode, WorkflowNode } from '../../nodes';
import {
  getLayoutContainerId,
  isContainerNode,
  isInputAttributeNode,
  isRiskCalculationNode,
  isSnippetInternalChildNode,
  isSnippetNode,
} from '../../nodes';
import {
  CANVAS_HORIZONTAL_PADDING,
  CANVAS_VERTICAL_PADDING,
  COLUMN_SPACING,
  XL_NODE_FIRST_ROW_HOIST,
  LEFT_CHILD_PADDING,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROW_SPACING,
} from '../constants';
import type { MaxPathLengthMap } from '../types';
import type {
  Allocation,
  NodeColumn,
  NodeDimensions,
  NodeMatrix,
  NodePosition,
} from './types';
import { defaultNodeMatrixEntry } from './constants';

/**
 * convert column and row numbers to pixels using the predefined dimensions
 */
export function getNodePosition(args: { row: number; column: number }): WorkflowNode['position'] {
  const { row, column } = args;

  const x = CANVAS_HORIZONTAL_PADDING + (column * (NODE_WIDTH + COLUMN_SPACING));
  const y = CANVAS_VERTICAL_PADDING + (row * (NODE_HEIGHT + ROW_SPACING));

  return { x, y };
}

function offsetFolderChildNode(position: XYPosition) {
  return { x: position.x + LEFT_CHILD_PADDING, y: position.y };
}

export function getNextAvailableRowIndex(
  column: NodeColumn,
  node: WorkflowNode,
) {
  const requiresOwnership = (getLayoutContainerId(node) !== null) || isContainerNode(node);
  const comparator = requiresOwnership
    ? (allocation: Allocation) => isReservedForNode(allocation, node)
    : isFree;

  for (let rowIndex = 0; rowIndex < column.length; rowIndex += 1) {
    if (comparator(column[rowIndex].allocation)) {
      return rowIndex;
    }
  }

  return -1;
}

function maxOccurrences(values: Iterable<number>): number {
  const frequencyMap = new Map<number, number>();
  let max = 0;

  for (const v of values) {
    const next = (frequencyMap.get(v) ?? 0) + 1;
    frequencyMap.set(v, next);
    if (next > max) max = next;
  }

  return max;
}

export function getDimensions(
  maxPathLengthMap: MaxPathLengthMap,
): NodeDimensions {
  // the furthest column = the longest node path
  const longestPath = Math.max(...maxPathLengthMap.values());

  // the maximum number of row in a graph = the most number of nodes located within a single column
  const largestRow = maxOccurrences(maxPathLengthMap.values());

  return {
    nodeWidth: longestPath + 1,
    nodeHeight: largestRow,
  };
}

// function getOwnerKey(entry: NodeMatrixEntry): ContainerNode['id'] | null {
//   return entry.allocation.owningId;
// }

// function isSnippetOwned(entry: NodeMatrixEntry): boolean {
//   const key = getOwnerKey(entry);
//   return key ? isActionIdOfType(key, ActionType.SNIPPET) : false;
// }

// function getAllocatedNodeId(entry: NodeMatrixEntry): SnippetNode['id'] | null {
//   return entry.allocation.allocatedNodeId;
// }

// function isSubGraphBoundary(a: NodeMatrixEntry, b: NodeMatrixEntry): boolean {
//   if (!a || !b) return false;
//   return getOwnerKey(a) !== getOwnerKey(b);
// }

// function isSnippetBoundary(a: NodeMatrixEntry, b: NodeMatrixEntry): boolean {
//   if (!isSubGraphBoundary(a, b)) return false;

//   const isASnippet = isActionIdOfType(getAllocatedNodeId(a), ActionType.SNIPPET);
//   const isBSnippet = isActionIdOfType(getAllocatedNodeId(b), ActionType.SNIPPET);

//   return isASnippet || isBSnippet;
// }

// function isCollapsedSnippetBoundary(a: WorkflowNode, b: WorkflowNode): boolean {
//   if (a && isCollapsedSnippetNode(a)) return true;
//   if (b && isCollapsedSnippetNode(b)) return true;

//   return false;
// }

export function deriveXYPositions(args: {
  matrix: NodeMatrix;
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>;
  hasInputAttributeNodeFolder: boolean;
}) {
  const { matrix, indexedNodes, hasInputAttributeNodeFolder } = args;

  // PASS 1: gradually build Y by walking rows within each column
  matrix.forEach(column => {
    let y = CANVAS_VERTICAL_PADDING;

    column.forEach((entry, rowIndex) => {
      const node = indexedNodes[entry.allocation.allocatedNodeId];

      // hoist XL nodes in the first row up slightly
      const isFirstRow = rowIndex === 0;
      const isXLNode = node && (isSnippetNode(node) || isRiskCalculationNode(node));
      if (isFirstRow && isXLNode) {
        y -= XL_NODE_FIRST_ROW_HOIST;
      }

      entry.position.y = y;

      const defaultSpacing = NODE_HEIGHT + ROW_SPACING;
      y += defaultSpacing;
    });
  });


  // PASS 2: set X by walking columns within each row
  const rowCount = matrix[0]?.length ?? 0;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    let x = CANVAS_HORIZONTAL_PADDING;

    matrix.forEach(column => {
      const entry = column[rowIndex];
      const node = indexedNodes[entry.allocation.allocatedNodeId];

      entry.position.x = x;

      if (node && hasInputAttributeNodeFolder && isInputAttributeNode(node)) {
        entry.position = offsetFolderChildNode(entry.position);
      }

      const defaultSpacing = NODE_WIDTH + COLUMN_SPACING;
      x += defaultSpacing;
    });
  }
}

export function createDefaultNodeMatrix(dimensions: NodeDimensions): NodeMatrix {
  const { nodeWidth, nodeHeight } = dimensions;

  return Array.from({ length: nodeWidth }, () => (
    Array.from({ length: nodeHeight }, () => structuredClone(defaultNodeMatrixEntry))
  ));
}

function isReservedForNode(allocation: Allocation, node: WorkflowNode) {
  const { owningId, allocatedNodeId } = allocation;
  const layoutId = getLayoutContainerId(node);
  if (layoutId !== null) {
    return !allocatedNodeId && (owningId === layoutId);
  }
  return !allocatedNodeId && (owningId === node.id);
}

function isFree(allocation: Allocation) {
  const { owningId, allocatedNodeId } = allocation;
  return !owningId && !allocatedNodeId;
}

/**
 * Returns whether or not a space is free in the postion matrix.
 * The node position is the top-left corner of the space described by the supplied node dimensions.
 */
export function isSpaceFree(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
}): boolean {
  const { matrix, position, dimensions } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  // Check all positions the node would occupy
  for (let col = startCol; col < startCol + nodeWidth; col += 1) {
    // out of bounds
    if (col >= matrix.length) return false;

    const rows = matrix[col];

    for (let row = startRow; row < startRow + nodeHeight; row += 1) {
      // out of bounds
      if (row >= rows.length) return false;

      // space is already occupied, space is not free
      if (rows[row].allocation.owningId) return false;
    }
  }

  return true;
}


/** Mutates the position matrix, marking the owning node id in each position */
function assignOwningNode(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  ownerId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    ownerId,
  } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  const endCol = startCol + nodeWidth;
  const endRow = startRow + nodeHeight;

  for (let col = startCol; col < endCol; col += 1) {
    for (let row = startRow; row < endRow; row += 1) {
      matrix[col][row].allocation.owningId = ownerId;
    }
  }
}

/**
 * Get the column for a node.
 * Columns are determined by the largest dependent path a node might have. if a node has
 * no dependents, it is assigned column 0.
 */
export function getNodeColumn(
  maxPathLengthMap: MaxPathLengthMap,
  node: WorkflowNode,
): number {
  if (isSnippetInternalChildNode(node)) {
    if (maxPathLengthMap.has(node.id)) {
      // remove snippet parent action from column computation
      return maxPathLengthMap.get(node.id) - 1;
    }
    return 0;
  }
  return maxPathLengthMap.get(node.id) ?? 0;
}

/**
 * Creates a position matrix, marking subgraph spaces as owned by their container nodes.
 */
export function allocateSubgraphOwnership(args: {
  matrix: NodeMatrix;
  nodes: WorkflowNode[];
  maxIncomingPathLengthMap: MaxPathLengthMap;
  subGraphDimensions: Record<SnippetNode['id'], NodeDimensions>;
}): NodeMatrix {
  const {
    matrix,
    nodes,
    maxIncomingPathLengthMap,
    subGraphDimensions,
  } = args;

  // STEP 1: mark subgraph ownership within the node matrix
  nodes.forEach(node => {
    if (!isContainerNode(node)) {
      // only mark spaces owned by node containers
      return;
    }
    const dimensions = subGraphDimensions[node.id];
    const column = getNodeColumn(maxIncomingPathLengthMap, node);

    let row = 0;
    while (!isSpaceFree({ matrix, position: { column, row }, dimensions })) {
      row += 1;
    }

    assignOwningNode({
      matrix,
      position: { column, row },
      dimensions,
      ownerId: node.id,
    });
  });

  return matrix;
}

export function allocate(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  nodeId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    nodeId,
  } = args;
  const { column, row } = position;

  const colEnd = column + dimensions.nodeWidth;
  const rowEnd = row + dimensions.nodeHeight;

  for (let rowIndex = row; rowIndex < rowEnd; rowIndex += 1) {
    for (let colIndex = column; colIndex < colEnd; colIndex += 1) {
      matrix[colIndex][rowIndex].allocation.allocatedNodeId = nodeId;
    }
  }
}

export function getSnippetNodeDimensions(
  subgraphDimensions: NodeDimensions,
): NodeDimensions {
  return { nodeWidth: subgraphDimensions.nodeWidth, nodeHeight: 1 };
}
